import _ from 'lodash';
import React, { Component } from 'react';

import { DefaultTimelineSamplingSizes, TimelineScopes } from '../../lib/constants';
import { allowTimelineScopeCurrent } from '../../lib/measureHelpers';

import * as ReportingPeriods from '../../lib/reportingPeriods';

import ComputedMeasureChart, { ComputedMeasureChartProps } from './ComputedMeasureChart';
import { Measure } from '../../types';

import '../measure-chart.scss';

export type ChartProps = ComputedMeasureChartProps;

// A high-level component to render a Performance Measures timeline chart. It is currently
// featured in the Measure landing page and in Measures embedded in Stories.
//
// All you need to use this component is a Measure object obtained from /api/measures_v1/<uid>.json
// and permission for the current user to see the Measure. The component itself will handle
// fetching data when needed.
//
// Internally, MeasureChart is a light wrapper around ComputedMeasureChart (a dumb component that
// expects all data as props). MeasureChart is also responsible for rendering the chart scope
// selector buttons (Current/All reporting periods), as changing scope involves temporarily
// modifying the Measure object and re-fetching data. Data fetching is handled by wrapping
// ComputedMeasureChart in the withComputedMeasure Higher Order Component.
export class MeasureChart extends Component<ChartProps> {
  // Take a look at ComputedMeasureChart props. We mostly pass
  // props straight through.
  static defaultProps = { measure: null };
  constructor(props: ChartProps) {
    super(props);
  }

  getTimelineScope() {
    return this.props.timelineScope;
  }

  // This method takes a measure and applies the current state.timelineScope to it.
  // Please note that this method is a noop if state.timelineScope is TimelineScopes.ALL.
  //
  // To minimize impact on the rest of the platform, we treat switching to the "current"
  // reporting period as applying these transforms on the measure:
  // 1. Reporting period changed to "open"
  // 2. Reporting period start date changed to the current reporting period's start date.
  // 3. Reporting period length changed to timelineSampling.
  getScopedMeasure = (measure: Measure) => {
    const timelineScope = this.getTimelineScope();

    if (timelineScope === TimelineScopes.ALL || !allowTimelineScopeCurrent(measure)) {
      return measure;
    }

    const reportingPeriod = measure?.metricConfig?.reportingPeriod;
    if (!ReportingPeriods.isConfigValid(reportingPeriod)) {
      return measure;
    }

    const period = ReportingPeriods.getLastOpenReportingPeriod(reportingPeriod!);

    const scopedMeasure = _.cloneDeep(measure);

    // Limit the measure to just _this_ reporting period. We're not interested in
    // historical data.
    scopedMeasure.metricConfig!.reportingPeriod!.startDateConfig.date =
      // TODO: is this a bug?
      period!.start.format('YYYY-MM-DD');

    // Set the size to the selected interval.
    const defaultSampling =
      // @ts-expect-error DefaultTimelineSamplingSizes is missing DAY in period size
      DefaultTimelineSamplingSizes[reportingPeriod!.size!];
    scopedMeasure.metricConfig!.reportingPeriod!.size =
      measure?.metricConfig?.display?.timelineSampling ?? defaultSampling;

    return scopedMeasure;
  };

  render() {
    const { measure } = this.props;
    const timelineScope = this.getTimelineScope();

    const scopedMeasure = measure ? this.getScopedMeasure(measure) : null;

    return (
      <div className="measure-chart">
        <ComputedMeasureChart {...this.props} measure={scopedMeasure} timelineScope={timelineScope} />
      </div>
    );
  }
}

export default MeasureChart;
